import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import logger from '../utils/logger';

/**
 * Google OAuth Callback Page
 * 
 * Google OAuth認証後のコールバックを受け取り、
 * トークンを親ウィンドウに送信してポップアップを閉じる
 * 
 * URLパラメータ:
 * - accessToken: アクセストークン
 * - refreshToken: リフレッシュトークン
 * 
 * @returns Google OAuth Callback component
 */
const GoogleCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const processCallback = async () => {
      try {
        const accessToken = searchParams.get('accessToken');
        const refreshToken = searchParams.get('refreshToken');
        const userId = searchParams.get('userId');
        const email = searchParams.get('email');
        const firstName = searchParams.get('firstName');
        const lastName = searchParams.get('lastName');
        const role = searchParams.get('role');
        const tenantId = searchParams.get('tenantId');

        logger.log('🔍 [GoogleCallback] 受信パラメータ:', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          userId,
          email,
          isPopup: !!window.opener,
          currentOrigin: window.location.origin,
          openerOrigin: window.opener?.location.origin || 'N/A'
        });

        if (!accessToken || !refreshToken) {
          throw new Error('認証トークンが見つかりません');
        }

        logger.log('✅ Google OAuth Callback受信:', { accessToken: accessToken.substring(0, 20) + '...' });

        // トークンを親ウィンドウに送信
        if (window.opener) {
          logger.log('📤 [GoogleCallback] 親ウィンドウへpostMessage送信開始');

          const payload = {
            type: 'GOOGLE_AUTH_SUCCESS',
            payload: {
              accessToken,
              refreshToken,
              user: {
                id: userId || '',
                email: email || '',
                firstName: firstName || '',
                lastName: lastName || '',
                name: `${firstName || ''} ${lastName || ''}`.trim(),
                role: role || 'staff',
                tenantId: tenantId || '',
              },
              expiresIn: 3600,
            },
          };

          logger.log('📤 [GoogleCallback] Payload:', { ...payload, payload: { ...payload.payload, accessToken: '***' } });

          window.opener.postMessage(payload, window.location.origin);

          logger.log('✅ 親ウィンドウにトークン送信完了');
          setStatus('success');

          // 2秒後にポップアップを閉じる
          setTimeout(() => {
            logger.log('🔒 [GoogleCallback] ポップアップを閉じます');
            window.close();
          }, 2000);
        } else {
          // ポップアップでない場合はエラー扱い
          logger.error('❌ [GoogleCallback] window.opener が null です。ポップアップとして開かれていません。');
          throw new Error('このページはポップアップとして開く必要があります。');
        }
      } catch (error) {
        logger.error('❌ Google OAuth Callback エラー:', error);
        setStatus('error');
        setErrorMessage(error instanceof Error ? error.message : 'Unknown error');

        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'GOOGLE_AUTH_ERROR',
              error: error instanceof Error ? error.message : 'Unknown error',
            },
            window.location.origin
          );

          // 3秒後にポップアップを閉じる
          setTimeout(() => {
            window.close();
          }, 3000);
        }
      }
    };

    processCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="h-16 w-16 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Google認証処理中...
            </h2>
            <p className="text-sm text-gray-600">
              しばらくお待ちください
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              認証成功！
            </h2>
            <p className="text-sm text-gray-600">
              このウィンドウは自動的に閉じます...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              認証エラー
            </h2>
            <p className="text-sm text-red-600 mb-4">
              {errorMessage}
            </p>
            <p className="text-sm text-gray-600">
              このウィンドウは自動的に閉じます...
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleCallback;
